import { useEffect, useRef, useState } from 'react';
import * as T from 'three';
import { OrbitControls } from 'three/addons/controls/OrbitControls.js';
import { LOCATIONS, type Character } from '@london-life/game/game';
import { AnimatedCharacter, type Motion } from '@london-life/game/characters';
import { renderSceneStill } from '@london-life/game/scene/still';
import { createSceneKit } from '@london-life/game/scene/kit';
export type Visitor = { id: string; character: Character; x: number; z: number };
type Props = {
  character: Character;
  position: { x: number; z: number };
  visitors: Visitor[];
  quality: 'low' | 'balanced';
  onLocation: (id: string) => void;
  onMove: (p: { x: number; z: number }) => void;
};
export function AvatarPreview({ character, onReady }: { character: Character; onReady?: (ready: boolean) => void }) {
  const ref = useRef<HTMLDivElement>(null),
    actor = useRef<AnimatedCharacter | null>(null);
  const currentCharacter = useRef(character),
    readyCallback = useRef(onReady);
  currentCharacter.current = character;
  readyCallback.current = onReady;
  const engine = useRef<{ replaceLook: (character: Character) => void } | null>(null);
  const [status, setStatus] = useState('Loading character…'),
    [failed, setFailed] = useState(false),
    [mode, setMode] = useState<'3d' | 'still' | null>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    let alive = true,
      ready = false;
    const k = createSceneKit(),
      scene = new T.Scene(),
      camera = new T.PerspectiveCamera(30, 1, 0.1, 50);
    camera.position.set(1.8, 2.1, 5.6);
    camera.lookAt(0, 1.2, 0);
    let renderer: T.WebGLRenderer | undefined, controls: OrbitControls | undefined;
    let canvas: HTMLCanvasElement;
    try {
      renderer = new T.WebGLRenderer({ alpha: true, antialias: true, powerPreference: 'low-power' });
      renderer.setPixelRatio(Math.min(devicePixelRatio, 1.5));
      canvas = renderer.domElement;
      setMode('3d');
    } catch {
      canvas = document.createElement('canvas');
      setMode('still');
    }
    canvas.setAttribute('aria-hidden', 'true');
    el.appendChild(canvas);
    if (renderer) {
      controls = new OrbitControls(camera, canvas);
      controls.target.set(0, 1.2, 0);
      controls.enableDamping = true;
      controls.enablePan = false;
      controls.enableZoom = false;
      controls.minPolarAngle = 0.9;
      controls.maxPolarAngle = 1.55;
      controls.update();
    }
    scene.add(new T.HemisphereLight('#fffbea', '#b8cbd7', 2));
    const light = new T.DirectionalLight('#ffffff', 2.5);
    light.position.set(4, 5, 5);
    scene.add(light);
    k.cylinder(0.8, 0.12, '#efd7ab', 0, -0.1, 0, scene);
    const resize = () => {
      const w = el.clientWidth,
        h = el.clientHeight;
      if (!w || !h) return;
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
      if (renderer) renderer.setSize(w, h);
      else if (ready) renderSceneStill(scene, camera, canvas, w, h);
    };
    engine.current = {
      replaceLook(next) {
        if (actor.current) {
          scene.remove(actor.current.group);
          actor.current.dispose();
        }
        const person = new AnimatedCharacter(next);
        actor.current = person;
        scene.add(person.group);
        ready = false;
        readyCallback.current?.(false);
        setFailed(false);
        setStatus('Loading character…');
        void person.ready
          .then(() => {
            if (alive && actor.current === person) {
              ready = true;
              resize();
              setStatus('');
              readyCallback.current?.(true);
            }
          })
          .catch((error) => {
            if (alive && actor.current === person) {
              ready = false;
              readyCallback.current?.(false);
              setFailed(true);
              setStatus('Your character could not load. Please try again.');
              console.error('London Life character preview failed:', error);
            }
          });
      },
    };
    resize();
    const observer = new ResizeObserver(resize);
    observer.observe(el);
    let frame = 0,
      last = 0;
    function tick(t: number) {
      frame = requestAnimationFrame(tick);
      if (t - last < 40 || document.hidden) return;
      const dt = Math.min((t - last) / 1000, 0.08);
      last = t;
      actor.current?.update(dt);
      controls?.update();
      renderer?.render(scene, camera);
    }
    if (renderer) frame = requestAnimationFrame(tick);
    return () => {
      alive = false;
      engine.current = null;
      cancelAnimationFrame(frame);
      observer.disconnect();
      actor.current?.dispose();
      actor.current = null;
      controls?.dispose();
      k.dispose();
      renderer?.dispose();
      renderer?.forceContextLoss();
      canvas.remove();
    };
  }, []);
  useEffect(() => {
    engine.current?.replaceLook(character);
  }, [
    character.skin,
    character.outfit,
    character.hair,
    character.body,
    character.look,
    character.skinColour,
    character.outfitColour,
    character.hairColour,
    character.buildScale,
  ]);
  return (
    <div className="avatar-preview-shell">
      <div className="avatar-preview" data-renderer={mode ?? 'loading'} aria-label="Your character preview">
        <div className="avatar-preview-canvas" ref={ref} />
        {status ? (
          <div className="avatar-preview-status" role={failed ? 'alert' : 'status'}>
            <span>{status}</span>
            {failed && (
              <button
                type="button"
                className="avatar-retry"
                onClick={() => engine.current?.replaceLook(currentCharacter.current)}
              >
                Try again
              </button>
            )}
          </div>
        ) : null}
      </div>
      <div className="avatar-preview-controls" aria-label="Preview animations">
        {(
          [
            ['idle', 'Stand'],
            ['walk', 'Walk'],
            ['run', 'Run'],
            ['wave', 'Wave'],
          ] as [Motion, string][]
        ).map(([motion, label]) => (
          <button
            type="button"
            key={motion}
            disabled={!!status || mode !== '3d'}
            onClick={() => actor.current?.setMotion(motion)}
          >
            {label}
          </button>
        ))}
      </div>
      <small className="avatar-preview-hint">
        {mode === 'still' ? 'Static preview · 3D is unavailable on this device' : 'Drag to turn · Try an animation'}
      </small>
    </div>
  );
}
export default function World(props: Props) {
  const container = useRef<HTMLDivElement>(null),
    latest = useRef(props);
  latest.current = props;
  const [failed, setFailed] = useState(false),
    [characterError, setCharacterError] = useState(false);
  useEffect(() => {
    const el = container.current;
    if (!el) return;
    const k = createSceneKit(),
      scene = new T.Scene();
    scene.background = new T.Color('#bad4dc');
    const camera = new T.OrthographicCamera(-43, 43, 30, -30, 0.1, 220);
    camera.position.set(48, 53, 65);
    camera.lookAt(0, 0, 0);
    let renderer: T.WebGLRenderer;
    try {
      renderer = new T.WebGLRenderer({ antialias: props.quality === 'balanced', powerPreference: 'low-power' });
    } catch {
      setFailed(true);
      return;
    }
    renderer.setPixelRatio(Math.min(devicePixelRatio, props.quality === 'low' ? 1 : 1.5));
    renderer.shadowMap.enabled = props.quality === 'balanced';
    renderer.shadowMap.type = T.PCFSoftShadowMap;
    renderer.setSize(el.clientWidth, el.clientHeight);
    el.appendChild(renderer.domElement);
    const controls = new OrbitControls(camera, renderer.domElement);
    controls.target.set(0, 0, 0);
    controls.enableDamping = true;
    controls.dampingFactor = 0.08;
    controls.minZoom = 0.7;
    controls.maxZoom = 3;
    controls.maxPolarAngle = Math.PI / 2.4;
    controls.minPolarAngle = 0.35;
    controls.enablePan = false;
    controls.mouseButtons = { LEFT: T.MOUSE.ROTATE, MIDDLE: T.MOUSE.DOLLY, RIGHT: T.MOUSE.ROTATE };
    scene.add(new T.HemisphereLight('#fffcf0', '#9abec9', 2.3));
    const sun = new T.DirectionalLight('#fff3d3', 3);
    sun.position.set(-30, 55, 15);
    sun.castShadow = true;
    sun.shadow.mapSize.set(1024, 1024);
    sun.shadow.camera.left = -45;
    sun.shadow.camera.right = 45;
    sun.shadow.camera.top = 40;
    sun.shadow.camera.bottom = -40;
    sun.shadow.normalBias = 0.08;
    scene.add(sun);
    const ground = k.box(64, 1.6, 50, '#e1dfcc', 0, -0.8, 0, scene);
    ground.userData.ground = true;
    k.box(64, 0.06, 7, '#71b6bc', 0, 0.035, 10, scene);
    k.box(64, 0.08, 6, '#58666d', 0, 0.08, -4, scene);
    k.box(5, 0.09, 25, '#58666d', 2, 0.09, -6.5, scene);
    k.box(64, 0.12, 0.6, '#c7c9bd', 0, 0.15, -0.7, scene);
    k.box(64, 0.12, 0.6, '#c7c9bd', 0, 0.15, -7.3, scene);
    for (let x = -30; x < 32; x += 4) k.box(1.8, 0.015, 0.1, '#e7e6d8', x, 0.14, -4, scene);
    k.box(6, 0.28, 14, '#dfd9c5', 2, 0.25, 10, scene);
    for (const x of [-1, 5]) {
      k.box(0.15, 0.5, 14, '#b7c0bb', x, 0.7, 10, scene);
      for (let z = 4; z < 18; z += 2) k.box(0.17, 1, 0.17, '#f7f3e5', x, 0.6, z, scene);
    }
    for (let z = 8; z <= 12; z += 2)
      for (let x = -28; x < 29; x += 7) k.box(2, 0.01, 0.035, '#d0e8de', x, 0.08, z, scene);
    function building(id: string, x: number, z: number, w: number, h: number, d: number, color: string, shop?: string) {
      const g = new T.Group();
      g.position.set(x, 0, z);
      g.userData.location = id;
      scene.add(g);
      k.box(w, h, d, color, 0, h / 2, 0, g);
      k.box(w + 0.35, 0.2, d + 0.35, '#efedda', 0, h, 0, g);
      k.box(w + 0.25, 0.4, d + 0.25, '#45575b', 0, h + 0.3, 0, g);
      for (let y = 1.6; y < h - 0.3; y += 1.5)
        for (let wx = -w / 2 + 0.7; wx < w / 2; wx += 1.3) {
          k.box(0.66, 0.82, 0.06, '#bdd9d8', wx, y, d / 2 + 0.04, g);
          k.box(0.82, 0.1, 0.15, '#e8decd', wx, y - 0.47, d / 2 + 0.08, g);
        }
      k.box(0.8, 1.3, 0.09, '#344b4d', 0.1, 0.7, d / 2 + 0.1, g);
      if (shop) {
        k.box(w, 0.75, 0.12, id === 'pub' ? '#234b43' : '#e95442', 0, 1.35, d / 2 + 0.16, g);
        k.text(shop, 0, h + 1.5, 0, g, Math.max(4.5, w + 1));
      }
      return g;
    }
    for (let n = 0; n < 4; n++) {
      const g = building('home', -26 + n * 3.7, -13, 3.5, 4.7, 5, ['#be8063', '#c78d6f', '#a86454', '#daa288'][n]);
      const roof = new T.Mesh(
        k.geo('roof', () => new T.ConeGeometry(2.65, 1.4, 4)),
        k.mat('#384e56'),
      );
      roof.position.set(0, 5.6, 0);
      roof.rotation.y = Math.PI / 4;
      roof.scale.z = 1.25;
      roof.castShadow = true;
      g.add(roof);
      k.box(0.4, 1.7, 0.45, '#a86553', 0.8, 6.1, -1, g);
      k.box(3.2, 0.12, 1.4, '#b9beac', 0, 0.1, 3.2, g);
    }
    k.text('HOUSE SHARE', -20, 8, -13, scene, 7);
    building('cafe', -5, -13, 5.5, 4, 5, '#eccaa0', 'THE DAILY GRIND');
    for (let n = 0; n < 3; n++) {
      k.cylinder(0.5, 0.7, '#f3eee0', -8 + n * 2, 0.7, -8.5, scene);
      k.cylinder(0.63, 0.1, '#d09365', -8 + n * 2, 1.1, -8.5, scene);
    }
    building('office', 12, -14, 8, 10, 6, '#73989a', 'CITY OFFICES');
    building('office', 5, -14, 4, 7, 5, '#829daa');
    const market = building('market', 24, -14, 8, 4, 5, '#be8f6a', 'CAMDEN MARKET');
    void market;
    for (let n = 0; n < 4; n++) {
      const g = new T.Group();
      g.position.set(18 + n * 2.7, 0, -9);
      g.userData.location = 'market';
      scene.add(g);
      k.box(2.4, 0.9, 1.5, '#cfac7d', 0, 0.5, 0, g);
      k.box(2.6, 0.15, 2, '#fcf2d7', 0, 2.3, 0, g);
      for (let j = 0; j < 4; j++) k.box(0.32, 0.17, 2, '#df6750', -0.97 + j * 0.65, 2.4, 0, g);
      for (const x of [-1, 1]) k.box(0.07, 2.3, 0.07, '#53634d', x, 1.2, 0, g);
      for (let j = 0; j < 3; j++) k.sphere(0.19, ['#e79e47', '#679348', '#e0704f'][j], -0.7 + j * 0.6, 1.15, 0, g);
    }
    building('pub', -23, 2, 7, 5, 5, '#b5745a', 'THE FOX & FINCH');
    k.box(7, 0.6, 0.2, '#24483d', -23, 1.2, 4.7, scene);
    building('gym', 24, 2, 6, 4.5, 5, '#759a92', 'THE GYM');
    k.box(14, 0.2, 9, '#a3bc76', -7, 0.2, 1, scene);
    k.box(1.3, 0.04, 9, '#ddd0a2', -7, 0.34, 1, scene);
    k.box(14, 0.04, 1.2, '#ddd0a2', -7, 0.35, 1, scene);
    const trees = [
      [-12, -1],
      [-11, 4],
      [-3, 4],
      [-3, -1],
      [-16, 5],
      [29, 6],
      [-28, -20],
      [-21, -20],
      [-12, -20],
      [17, -20],
      [25, -20],
      [-27, 18],
      [-23, 20],
      [24, 20],
      [28, 15],
    ];
    for (const [x, z] of trees) {
      k.cylinder(0.16, 1.7, '#88704e', x, 0.9, z, scene);
      k.sphere(1.25, '#719a66', x, 2.4, z, scene);
      k.sphere(0.85, '#8cae70', x + 0.5, 3, z, scene);
    }
    for (const [x, z] of [
      [-10, 2],
      [-3, 1],
      [8, 17],
    ]) {
      k.box(1.8, 0.16, 0.5, '#9e7350', x, 0.7, z, scene);
      k.box(1.8, 0.7, 0.12, '#9e7350', x, 1, z - 0.22, scene);
      for (const dx of [-0.65, 0.65]) k.box(0.1, 0.7, 0.4, '#3d4c4e', x + dx, 0.35, z, scene);
    }
    const tube = building('tube', 9, 2, 5, 1.7, 4, '#d1d1b8');
    k.text('UNDERGROUND', 0, 3, 0, tube, 5.7, '#fffdf5', '#254c87');
    const roundel = new T.Mesh(
      k.geo('roundel', () => new T.TorusGeometry(0.7, 0.15, 8, 24)),
      k.mat('#d84736'),
    );
    roundel.position.set(0, 4.5, 0);
    tube.add(roundel);
    k.box(1.8, 0.3, 0.15, '#254c87', 0, 4.5, 0, tube);
    const tower = new T.Group();
    tower.position.set(-19, 0, 19);
    tower.userData.location = 'bigben';
    scene.add(tower);
    k.box(3, 10, 3, '#d1b785', 0, 5, 0, tower);
    for (let n = 0; n < 4; n++) k.box(3.3, 0.22, 3.3, '#eddbab', 0, 1 + n * 2.3, 0, tower);
    k.box(3.8, 2.8, 3.8, '#d9c59b', 0, 11.4, 0, tower);
    for (const facing of [0, Math.PI / 2, Math.PI, (3 * Math.PI) / 2]) {
      const clock = new T.Group();
      clock.rotation.y = facing;
      tower.add(clock);
      const disk = new T.Mesh(
        k.geo('clock', () => new T.CircleGeometry(1, 24)),
        k.mat('#fbf2d6'),
      );
      disk.position.set(0, 11.5, 1.92);
      clock.add(disk);
      k.box(0.05, 0.8, 0.04, '#3f4642', 0, 11.8, 1.96, clock);
      const hand = k.box(0.65, 0.05, 0.04, '#3f4642', 0.28, 11.5, 1.96, clock);
      hand.rotation.z = -0.4;
    }
    k.box(3.4, 0.6, 3.4, '#687268', 0, 13.1, 0, tower);
    const spire = new T.Mesh(
      k.geo('spire', () => new T.ConeGeometry(2, 3.3, 4)),
      k.mat('#4a655e'),
    );
    spire.rotation.y = Math.PI / 4;
    spire.position.y = 14.95;
    tower.add(spire);
    k.cylinder(0.12, 1.3, '#c6ad70', 0, 17, 0, tower);
    for (let n = 0; n < 6; n++) building('bigben', -27 + n * 2.2, 20, 2, 3.4, 4, '#c8b48c');
    k.text('WESTMINSTER', -17, 7, 16, scene, 7);
    const wheel = new T.Group();
    wheel.position.set(17, 7.5, 19);
    wheel.userData.location = 'eye';
    scene.add(wheel);
    const ring = new T.Mesh(
      k.geo('wheel', () => new T.TorusGeometry(6, 0.16, 8, 48)),
      k.mat('#e9f0e8'),
    );
    wheel.add(ring);
    for (let n = 0; n < 12; n++) {
      const a = (n * Math.PI) / 6;
      const spoke = k.box(0.06, 12, 0.06, '#e3eeea', 0, 0, 0, wheel);
      spoke.rotation.z = a;
      const pod = k.sphere(0.42, '#b2e1d9', Math.cos(a) * 6, Math.sin(a) * 6, 0, wheel);
      pod.userData.pod = true;
    }
    for (const dx of [-2.5, 2.5]) {
      const support = k.box(0.2, 8, 0.2, '#ecf0e5', 17 + dx, 3.8, 19, scene);
      support.rotation.z = dx > 0 ? 0.6 : -0.6;
    }
    k.text('SOUTH BANK', 15, 3, 14, scene, 6);
    const bus = new T.Group();
    k.box(4, 2, 1.8, '#d64c3b', 0, 1.3, 0, bus);
    k.box(3.5, 0.55, 0.04, '#afccd1', 0, 1.85, 0.93, bus);
    k.box(3.5, 0.5, 0.04, '#afccd1', 0, 0.95, 0.93, bus);
    k.box(3.5, 0.55, 0.04, '#afccd1', 0, 1.85, -0.93, bus);
    for (const x of [-1.2, 1.2])
      for (const z of [-0.93, 0.93]) {
        const w = k.cylinder(0.34, 0.16, '#263945', x, 0.4, z, bus);
        w.rotation.x = Math.PI / 2;
      }
    bus.position.set(-22, 0, -5.5);
    scene.add(bus);
    for (let x = -28; x < 30; x += 10) {
      k.cylinder(0.065, 2.8, '#3e595f', x, 1.4, -0.4, scene);
      k.sphere(0.18, '#fff2bb', x, 2.85, -0.4, scene);
    }
    k.box(0.6, 1.4, 0.6, '#de4b3b', -17, 0.75, -0.2, scene);
    let alive = true;
    setFailed(false);
    setCharacterError(false);
    const actors = new Set<AnimatedCharacter>();
    const makePerson = (character: Character, name = false) => {
      const actor = new AnimatedCharacter(character);
      actors.add(actor);
      void actor.ready.catch(() => {
        if (alive) setCharacterError(true);
      });
      if (name) k.text(character.name, 0, 3, 0, actor.group, 3.4);
      return actor;
    };
    const removePerson = (actor: AnimatedCharacter) => {
      scene.remove(actor.group);
      actor.group.traverse((o) => {
        if (o instanceof T.Sprite) (o.material as T.Material).dispose();
      });
      actor.dispose();
      actors.delete(actor);
    };
    let player = makePerson(props.character),
      avatar = player.group,
      charKey = JSON.stringify(props.character);
    const target = new T.Vector3(props.position.x, 0, props.position.z);
    avatar.position.copy(target);
    scene.add(avatar);
    const marker = new T.Mesh(
      k.geo('marker', () => new T.OctahedronGeometry(0.35)),
      k.mat('#ffc856'),
    );
    marker.position.y = 3;
    avatar.add(marker);
    const npcs = Array.from({ length: 5 }, (_, n) => {
      const actor = makePerson({
        name: ['Maya', 'Alex', 'Sam', 'Ellie', 'Jamie'][n],
        skin: n % 5,
        outfit: (n + 1) % 6,
        hair: n % 4,
        body: n % 2 ? 'male' : 'female',
        origin: 'fresh',
        trait: 'Social',
      });
      actor.group.position.set(-24 + n * 10, 0, -1);
      scene.add(actor.group);
      return actor;
    });
    const guests = new Map<string, { actor: AnimatedCharacter; key: string }>();
    let remoteTarget = props.position,
      frame = 0,
      last = 0,
      lastMove = 0;
    const keys = new Set<string>();
    const down = (e: KeyboardEvent) => {
      if ((e.target as HTMLElement)?.matches('input,textarea,select') || document.querySelector('[role=dialog]'))
        return;
      if (['w', 'a', 's', 'd', 'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Shift'].includes(e.key)) {
        keys.add(e.key);
        e.preventDefault();
      }
      if (e.key.toLowerCase() === 'e') {
        const l = [...LOCATIONS].sort(
          (a, b) =>
            Math.hypot(a.x - avatar.position.x, a.z - avatar.position.z) -
            Math.hypot(b.x - avatar.position.x, b.z - avatar.position.z),
        )[0];
        if (Math.hypot(l.x - avatar.position.x, l.z - avatar.position.z) < 10) latest.current.onLocation(l.id);
      }
    };
    const up = (e: KeyboardEvent) => keys.delete(e.key);
    const blur = () => keys.clear();
    window.addEventListener('keydown', down);
    window.addEventListener('keyup', up);
    window.addEventListener('blur', blur);
    let start: { x: number; y: number } | null = null;
    const pointerDown = (e: PointerEvent) => {
      start = { x: e.clientX, y: e.clientY };
    };
    const raycaster = new T.Raycaster(),
      pointer = new T.Vector2();
    const click = (e: PointerEvent) => {
      if (!start || Math.hypot(e.clientX - start.x, e.clientY - start.y) > 6) return;
      const rect = renderer.domElement.getBoundingClientRect();
      pointer.set(((e.clientX - rect.left) / rect.width) * 2 - 1, (-(e.clientY - rect.top) / rect.height) * 2 + 1);
      raycaster.setFromCamera(pointer, camera);
      const hits = raycaster.intersectObjects(scene.children, true);
      for (const hit of hits) {
        let o: T.Object3D | null = hit.object;
        while (o) {
          if (o.userData.location) {
            latest.current.onLocation(o.userData.location);
            return;
          }
          o = o.parent;
        }
        if (hit.object === ground) {
          target.set(Math.max(-30, Math.min(30, hit.point.x)), 0, Math.max(-23, Math.min(23, hit.point.z)));
          return;
        }
      }
    };
    renderer.domElement.addEventListener('pointerdown', pointerDown);
    renderer.domElement.addEventListener('pointerup', click);
    const resize = () => {
      const w = el.clientWidth,
        h = el.clientHeight;
      renderer.setSize(w, h);
      const v = 30;
      camera.left = (-v * w) / h;
      camera.right = (v * w) / h;
      camera.top = v;
      camera.bottom = -v;
      camera.updateProjectionMatrix();
    };
    resize();
    const observer = new ResizeObserver(resize);
    observer.observe(el);
    function animate(t: number) {
      frame = requestAnimationFrame(animate);
      if (document.hidden || t - last < 1000 / (props.quality === 'low' ? 24 : 30)) return;
      const dt = Math.min((t - last) / 1000, 0.08);
      last = t;
      const now = latest.current;
      if (now.position !== remoteTarget) {
        remoteTarget = now.position;
        target.set(now.position.x, 0, now.position.z);
      }
      const ck = JSON.stringify(now.character);
      if (ck !== charKey) {
        const pos = avatar.position.clone(),
          rotation = avatar.rotation.y;
        avatar.remove(marker);
        removePerson(player);
        player = makePerson(now.character);
        avatar = player.group;
        avatar.position.copy(pos);
        avatar.rotation.y = rotation;
        avatar.add(marker);
        scene.add(avatar);
        charKey = ck;
      }
      let dx = 0,
        dz = 0;
      if (keys.has('w') || keys.has('ArrowUp')) {
        dx -= 1;
        dz -= 1;
      }
      if (keys.has('s') || keys.has('ArrowDown')) {
        dx += 1;
        dz += 1;
      }
      if (keys.has('a') || keys.has('ArrowLeft')) {
        dx -= 1;
        dz += 1;
      }
      if (keys.has('d') || keys.has('ArrowRight')) {
        dx += 1;
        dz -= 1;
      }
      const moveSpeed = keys.has('Shift') ? 5.5 : 3.2;
      if (dx || dz) {
        const v = new T.Vector3(dx, 0, dz).normalize().multiplyScalar(dt * moveSpeed);
        target.copy(avatar.position).add(v);
      }
      target.x = Math.max(-30, Math.min(30, target.x));
      target.z = Math.max(-23, Math.min(23, target.z));
      if (target.z > 6.5 && target.z < 13.5 && (target.x < -0.5 || target.x > 4.5))
        target.z = avatar.position.z < 10 ? 6.4 : 13.6;
      const diff = target.clone().sub(avatar.position);
      if (diff.length() > 0.025) {
        const angle = Math.atan2(diff.x, diff.z),
          delta = Math.atan2(Math.sin(angle - avatar.rotation.y), Math.cos(angle - avatar.rotation.y));
        avatar.rotation.y += delta * Math.min(1, dt * 12);
        const distance = diff.length();
        avatar.position.add(diff.normalize().multiplyScalar(Math.min(dt * moveSpeed, distance)));
        player.setMotion(keys.has('Shift') ? 'run' : 'walk');
      } else player.setMotion('idle');
      marker.rotation.y = t * 0.002;
      marker.position.y = 3 + Math.sin(t * 0.003) * 0.1;
      if (t - lastMove > 750) {
        latest.current.onMove({ x: avatar.position.x, z: avatar.position.z });
        lastMove = t;
      }
      bus.position.x = ((t * 0.0025 + 20) % 60) - 30;
      wheel.rotation.z = t * 0.000012;
      for (let n = 0; n < npcs.length; n++) {
        const actor = npcs[n],
          p = actor.group,
          next = -24 + n * 10 + Math.sin(t * 0.00015 + n) * 2,
          velocity = (next - p.position.x) / dt;
        p.position.x = next;
        if (Math.abs(velocity) > 0.06) {
          p.rotation.y = velocity > 0 ? Math.PI / 2 : -Math.PI / 2;
          actor.setMotion('walk', Math.max(0.35, Math.abs(velocity) / 3.2));
        } else actor.setMotion('idle');
      }
      const ids = new Set(now.visitors.map((v) => v.id));
      for (const [id, p] of guests)
        if (!ids.has(id)) {
          removePerson(p.actor);
          guests.delete(id);
        }
      for (const v of now.visitors) {
        const key = JSON.stringify(v.character);
        let p = guests.get(v.id);
        if (!p || p.key !== key) {
          if (p) removePerson(p.actor);
          p = { actor: makePerson(v.character, true), key };
          guests.set(v.id, p);
          p.actor.group.position.set(v.x, 0, v.z);
          scene.add(p.actor.group);
        }
        const group = p.actor.group,
          delta = new T.Vector3(v.x - group.position.x, 0, v.z - group.position.z),
          distance = delta.length();
        if (distance > 0.025) {
          group.rotation.y = Math.atan2(delta.x, delta.z);
          const step = Math.min(distance, dt * 5.5);
          group.position.add(delta.normalize().multiplyScalar(step));
          p.actor.setMotion(step / dt > 4 ? 'run' : 'walk', Math.min(1.5, Math.max(0.5, step / dt / 3.2)));
        } else p.actor.setMotion('idle');
      }
      actors.forEach((actor) => actor.update(dt));
      controls.update();
      renderer.render(scene, camera);
    }
    frame = requestAnimationFrame(animate);
    return () => {
      alive = false;
      cancelAnimationFrame(frame);
      observer.disconnect();
      actors.forEach((actor) => actor.dispose());
      actors.clear();
      window.removeEventListener('keydown', down);
      window.removeEventListener('keyup', up);
      window.removeEventListener('blur', blur);
      renderer.domElement.removeEventListener('pointerdown', pointerDown);
      renderer.domElement.removeEventListener('pointerup', click);
      controls.dispose();
      scene.traverse((o) => {
        if (o instanceof T.Sprite) (o.material as T.Material).dispose();
      });
      k.dispose();
      renderer.dispose();
      renderer.domElement.remove();
    };
  }, [props.quality]);
  return (
    <div className="world" ref={container} aria-label="Interactive 3D London neighbourhood">
      {characterError && !failed && (
        <div className="character-load-error" role="status">
          Characters could not load. Refresh to try again.
        </div>
      )}
      {failed && (
        <div className="world-error">
          3D graphics are unavailable on this device. You can still explore and play using the Places menu.
        </div>
      )}
    </div>
  );
}
